import { Text } from '@mantine/core';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { dimValue } from '@/shared/surfaces/InteractionRecordDrawer';
import { formatCount, formatTimestamp, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';

/**
 * The token-centric interaction columns — consumption (in / out / total tokens)
 * plus allocated cost, rather than the classification cut (task, difficulty,
 * confidence). Shared by the Spend › Tokens activity feed and the token topic
 * drill-in evidence tables so both read as spend, not classification.
 */
export const TOKEN_USAGE_INTERACTION_COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'person',
        header: 'Person',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => <Text size="xs">{dimValue(r, 'user_department')}</Text>,
    },
    {
        key: 'model',
        header: 'Model',
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'category',
        header: 'Category',
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'in',
        header: 'In',
        align: 'right',
        sortValue: (r) => r.inputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatCount(r.inputTokens)}
            </Text>
        ),
    },
    {
        key: 'out',
        header: 'Out',
        align: 'right',
        sortValue: (r) => r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatCount(r.outputTokens)}
            </Text>
        ),
    },
    {
        key: 'total',
        header: 'Total',
        align: 'right',
        sortValue: (r) => r.inputTokens + r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatCount(r.inputTokens + r.outputTokens)}
            </Text>
        ),
    },
    {
        key: 'cost',
        header: 'Cost',
        align: 'right',
        sortValue: (r) => r.allocatedCostUsd,
        render: (r) => (
            <Text size="xs" ff="monospace" c={r.allocatedCostUsd > 0 ? undefined : 'dimmed'}>
                {r.allocatedCostUsd > 0 ? formatUsd(r.allocatedCostUsd) : '—'}
            </Text>
        ),
    },
];
